import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { CONTAINER, EYEBROW, NAMESPACE, Rich, SECTION } from '../components/ui';
import type { GeoData } from '../engine/geo';
import { initMultiInternet } from '../engine/multi-internet';
import { buildMiStrings } from '../strings';

// The design's resting state: Fibra + Wi-Fi 6 active (800 Mb/s combined), the other four in reserve. The
// engine swaps the six rows for the visitor's country providers as soon as it boots.
const CONNS: { id: string; mbps: number; on: boolean; icon: ReactNode }[] = [
  {
    id: 'fibra',
    mbps: 500,
    on: true,
    icon: (
      <>
        <path d='M3 18c4.75 0 5-11.5 9.75-11.5H14' />
        <circle cx='16' cy='6.5' r='2' />
        <path d='M19 3.5l1.25-1.25M19.75 6.5H21.5M19 9.5l1.25 1.25' />
      </>
    ),
  },
  {
    id: 'wifi',
    mbps: 300,
    on: true,
    icon: (
      <>
        <path d='M3 9.75a13 13 0 0 1 18 0M6.25 13a8.5 8.5 0 0 1 11.5 0M9.5 16.25a4.25 4.25 0 0 1 5 0' />
        <circle cx='12' cy='19.75' r='.9' />
      </>
    ),
  },
  {
    id: 'g5',
    mbps: 150,
    on: false,
    icon: (
      <>
        <rect x='6.5' y='2.75' width='11' height='18.5' rx='2.25' />
        <path d='M10.5 18.25h3M8.75 12.25a4.75 4.75 0 0 1 6.5 0' />
        <circle cx='12' cy='14.75' r='.9' />
      </>
    ),
  },
  {
    id: 'cabo',
    mbps: 250,
    on: false,
    icon: (
      <>
        <path d='M5 3h14v9.5h-3.5v3h-7v-3H5z' />
        <path d='M8.5 3v3M12 3v3M15.5 3v3' />
        <path d='M12 15.5v5.5' />
      </>
    ),
  },
  {
    id: 'isp2',
    mbps: 200,
    on: false,
    icon: (
      <>
        <rect x='3' y='13' width='18' height='7.5' rx='2' />
        <path d='M6.5 16.75h.01M9.75 16.75h.01M16.5 13V6.25M13.25 6a4.5 4.5 0 0 1 6.5 0' />
      </>
    ),
  },
  {
    id: 'g4',
    mbps: 60,
    on: false,
    icon: (
      <>
        <rect x='3' y='14.5' width='3' height='6.5' rx='.9' />
        <rect x='8.2' y='10.75' width='3' height='10.25' rx='.9' />
        <rect x='13.4' y='7' width='3' height='14' rx='.9' />
        <rect x='18.6' y='3' width='3' height='18' rx='.9' />
      </>
    ),
  },
];

interface MultiInternetProps {
  geo: GeoData | null;
  onCta: () => void;
}

export function MultiInternet({ geo, onCta }: MultiInternetProps) {
  const { t } = useTranslation(NAMESPACE);
  const { locale = 'pt' } = useRouter();
  const rootRef = useRef<HTMLElement>(null);
  const strings = buildMiStrings(t, locale);
  const names = CONNS.map((conn) => t(`mi.conns.${conn.id}.name`));

  useEffect(() => {
    if (!geo || !rootRef.current) return undefined;
    return initMultiInternet({ root: rootRef.current, geo, strings: buildMiStrings(t, locale) });
  }, [geo, t, locale]);

  const combined = `${t('mi.conns.fibra.name')} + ${t('mi.conns.wifi.name')}`;

  return (
    <section className={SECTION} id='multi-internet' aria-labelledby='mi-kicker'>
      <div className='pointer-events-none absolute inset-0 z-[-1] overflow-hidden' aria-hidden='true'>
        <span className='sec__grid' />
        <span className='sec__halo sec__halo--r' />
      </div>
      <div className={CONTAINER}>
        <p className={EYEBROW} data-reveal>
          {t('mi.eyebrow')}
        </p>
        <h2 className='mt-4 max-w-[44em] break-words text-[clamp(1.125rem,.95rem+.8vw,1.5rem)] font-medium leading-[1.35] tracking-[-.015em] text-[#9AA3B5] [&_b]:font-bold [&_b]:text-white' id='mi-kicker' data-reveal>
          <Rich i18nKey='mi.kicker' />
        </h2>
      </div>

      <div className={`${CONTAINER} relative mt-[clamp(1.5rem,1rem+2vw,2.5rem)]`} id='mi-hold'>
        <div className='np-mi' data-np-comp='mi'>
          <section ref={rootRef} className='mc' aria-labelledby='mi-title' data-mc>
            <div className='mc-card'>
              <header className='mc-head'>
                <span className='eyebrow'>{t('mi.label')}</span>
                <h3 className='mc-title' id='mi-title'>
                  <span className='mc-title-a'>
                    {t('mi.title_a')} <span className='mc-title-b'>{t('mi.title_b')}</span>
                  </span>{' '}
                  <em>{t('mi.title_em')}</em>
                </h3>
                <p className='mc-lead'>
                  <span className='x-desk'>
                    <Rich i18nKey='mi.lead_desk' />
                  </span>
                  <span className='x-mobile'>
                    <Rich i18nKey='mi.lead_mobile' />
                  </span>
                </p>
                <ul className='mc-facts'>
                  <li>
                    <svg viewBox='0 0 24 24' aria-hidden='true'>
                      <path d='M12 3.5 5 6.25v5.5c0 4.4 3 7.6 7 8.75 4-1.15 7-4.35 7-8.75v-5.5z' />
                      <path d='M12.75 8.5 10 12.5h4l-2.75 4' />
                    </svg>
                    <span>
                      <Rich i18nKey='mi.fact_takeover' />
                    </span>
                  </li>
                  <li>
                    <svg viewBox='0 0 24 24' aria-hidden='true'>
                      <circle cx='9.25' cy='8.25' r='3.25' />
                      <path d='M2.75 19.5c0-3.15 2.9-5.25 6.5-5.25s6.5 2.1 6.5 5.25' />
                      <path d='M16.25 5.4a3.05 3.05 0 0 1 0 5.7M17.9 14.6c2.15.62 3.35 2.4 3.35 4.9' />
                    </svg>
                    <span>
                      <Rich i18nKey='mi.fact_players' />
                    </span>
                  </li>
                </ul>
              </header>

              <div className='mc-proof' role='group' aria-label={t('mi.proof_aria')} data-proof>
                <p className='sr-only' data-proof-sr>
                  {t('mi.proof_sr_initial')}
                </p>
                <div className='mc-proof-rows' aria-hidden='true'>
                  <div className='mc-proof-item mc-proof-item--bad'>
                    <span className='mc-proof-line'>
                      <small>{t('mi.without')}</small>
                      <span className='mc-proof-sp' />
                      <strong>
                        <span data-p-base>500</span>
                        <span className='u' data-p-base-u>
                          {strings.mbps}
                        </span>
                      </strong>
                    </span>
                    <span className='mc-proof-bar'>
                      <span className='mc-proof-fill' data-p-fill />
                    </span>
                  </div>
                  <div className='mc-proof-item mc-proof-item--good'>
                    <span className='mc-proof-line'>
                      <small>{t('mi.with')}</small>
                      <span className='mc-proof-sp' />
                      <span className='mc-proof-pct' data-p-pct>
                        +60%
                      </span>
                      <strong>
                        <span data-p-sum>800</span>
                        <span className='u' data-p-sum-u>
                          {strings.mbps}
                        </span>
                      </strong>
                    </span>
                    <span className='mc-proof-bar'>
                      <span className='mc-proof-fill' data-p-fill-good />
                    </span>
                  </div>
                </div>
                <p className='mc-proof-note' aria-hidden='true'>
                  <span className='mc-proof-tag' data-p-drops>
                    {strings.drops(0)}
                  </span>
                  <Rich i18nKey='mi.proof_note' />
                </p>
              </div>

              <p className='sr-only' id='mi-desc' data-mi-desc>
                {strings.description(strings.list(names))}
              </p>
              {/* the diagram is geometry (PC → cards → NoPing → server, left to right), not reading order */}
              <div className='mc-stage' data-stage role='group' aria-label={t('mi.stage_aria')} aria-describedby='mi-desc' dir='ltr'>
                <p className='sr-only' data-sr-now>
                  {t('mi.sr_now_initial')}
                </p>
                <svg className='mc-wires' data-wires aria-hidden='true' focusable='false' />
                <div className='mc-packets' data-packets aria-hidden='true' />
                <div className='mc-nojs-rail' aria-hidden='true' />
                <div className='mc-node mc-node--pc' data-node='pc'>
                  <div className='mc-node-icon' aria-hidden='true'>
                    <svg viewBox='0 0 40 40'>
                      <rect x='5' y='7' width='30' height='20' rx='3' />
                      <path d='M14 34h12M20 27v7' />
                      <circle cx='20' cy='17' r='5' />
                      <path d='M20 9v3.5M20 21.5V25M12 17h3.5M24.5 17H28' />
                      <circle className='f' cx='20' cy='17' r='1.4' />
                    </svg>
                  </div>
                  <div className='mc-node-label'>
                    <strong>{t('mi.pc')}</strong>
                    <small>{t('mi.your_internet')}</small>
                  </div>
                </div>
                <div className='mc-routes-wrap'>
                  <div className='mc-key'>
                    <span className='mc-key-col' aria-hidden='true' data-key>
                      <b>
                        <span data-count>2</span> {t('mi.of_six')} <span data-count-w>{strings.activeWord(2)}</span>
                      </b>
                    </span>
                    <p className='mc-hint' data-hint aria-hidden='true'>
                      <svg className='x-touch' viewBox='0 0 24 24'>
                        <circle className='dot' cx='15' cy='15' r='2.4' />
                        <path d='M9.17 17.12A6.2 6.2 0 0 1 17.12 9.17M5.04 18.63A10.6 10.6 0 0 1 18.63 5.04' />
                      </svg>
                      <svg className='x-hover' viewBox='0 0 24 24'>
                        <path d='M5 3l14 8.2-6.4 1.6L9.4 19z' />
                      </svg>
                      <svg className='x-after' viewBox='0 0 24 24'>
                        <path d='M4.5 12.5l4.5 4.5L19.5 6.5' />
                      </svg>
                      <span className='x-touch'>{t('mi.hint_touch')}</span>
                      <span className='x-hover'>{t('mi.hint_hover')}</span>
                      <span className='x-after'>{t('mi.hint_after')}</span>
                    </p>
                  </div>
                  <svg className='mc-defs' aria-hidden='true' focusable='false'>
                    <defs>
                      <linearGradient id='mi-spg' x1='0' y1='0' x2='0' y2='1'>
                        <stop className='g0' offset='0' />
                        <stop className='g1' offset='1' />
                      </linearGradient>
                    </defs>
                  </svg>
                  <ul className='mc-routes' data-routes aria-label={t('mi.routes_aria')}>
                    {CONNS.map((conn, index) => (
                      <li key={conn.id}>
                        <button className={`mc-route ${conn.on ? 'is-on' : 'is-off'}`} type='button' data-conn={conn.id} aria-pressed={conn.on} aria-label={strings.connAria(names[index]!, conn.mbps) + (conn.on ? strings.connAriaOn : strings.connAriaOff)}>
                          <span className='mc-route-cc' aria-hidden='true'>
                            <svg viewBox='0 0 24 24'>{conn.icon}</svg>
                          </span>
                          <span className='mc-route-body'>
                            <span className='mc-route-city'>{names[index]}</span>
                            <span className='mc-route-meta'>
                              <span className='mc-route-kind'>{t(`mi.conns.${conn.id}.kind`)}</span>
                              <span className='mc-route-tag' data-tag>
                                {conn.on ? strings.tagOn : strings.tagOff}
                              </span>
                            </span>
                          </span>
                          <svg className='mc-route-spark' viewBox='0 0 52 18' aria-hidden='true' focusable='false'>
                            <polygon className='sp-fill' points='' />
                            <polyline points='' />
                            <circle className='sp-dot' r='1.75' />
                          </svg>
                          <span className='mc-route-lat'>
                            <span className='mc-route-ms'>
                              <span data-mb>{conn.mbps}</span>
                              <span className='u'>{strings.mbps}</span>
                            </span>
                            <span className='mc-route-delta' data-delta>
                              {conn.on ? strings.deltaOn : strings.deltaOff}
                            </span>
                          </span>
                          <span className='mc-route-tg' aria-hidden='true'>
                            <svg viewBox='0 0 24 24'>
                              <path className='t-off' d='M12 5.5v13M5.5 12h13' />
                              <path className='t-on' d='M5 12.5l4.5 4.5L19 7' />
                              <path className='t-down' d='M19 12a7 7 0 1 1-2.05-4.95M19 4.5v3.5h-3.5' />
                            </svg>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className='mc-node mc-node--np' data-node='np'>
                  <div className='mc-node-icon' aria-hidden='true'>
                    <svg viewBox='0 0 40 40'>
                      <path d='M5 11h6c6 0 7 9 13 9' />
                      <path d='M5 29h6c6 0 7-9 13-9' />
                      <path d='M5 20h19' />
                      <path d='M24 20h10.5M30.5 16.5 34.5 20l-4 3.5' />
                      <circle className='f' cx='24' cy='20' r='2.3' />
                    </svg>
                    <span className='mi-burst' />
                  </div>
                  <div className='mc-node-label'>
                    <strong>{t('mi.np')}</strong>
                    <small className='mc-node-sub'>
                      <span data-np-sub>{strings.npSubCombines}</span>
                      <span className='mc-node-ghost' aria-hidden='true'>
                        {strings.npSubTookOver}
                      </span>
                      <span className='mc-node-ghost' aria-hidden='true'>
                        {strings.npSubCombines}
                      </span>
                    </small>
                  </div>
                </div>
                <div className='mc-node mc-node--game' data-node='game'>
                  <div className='mc-node-icon' aria-hidden='true'>
                    <svg viewBox='0 0 40 40'>
                      <rect x='7' y='7' width='26' height='10' rx='2.5' />
                      <rect x='7' y='23' width='26' height='10' rx='2.5' />
                      <path d='M12 12h6M12 28h6' />
                      <circle className='f' cx='27' cy='12' r='1.6' />
                      <circle className='f' cx='27' cy='28' r='1.6' />
                    </svg>
                  </div>
                  <div className='mc-node-label'>
                    <strong>{t('mi.game')}</strong>
                    <small className='mc-node-sub'>
                      <span data-game-sub>{strings.gameSubContinuous}</span>
                      <span className='mc-node-ghost' aria-hidden='true'>
                        {strings.gameSubContinuous}
                      </span>
                    </small>
                  </div>
                </div>
                <div className='mc-backup' data-backup aria-hidden='true'>
                  <p className='mc-direct-key'>{t('mi.backup_key')}</p>
                  <div className='mc-backup-row'>
                    <svg className='mc-backup-fan' viewBox='0 0 44 22' aria-hidden='true' focusable='false'>
                      <path className='bf-l' d='M22 3 2 19M22 3 6.4 19M22 3 10.9 19M22 3 15.3 19M22 3 19.8 19M22 3 24.2 19M22 3 28.7 19M22 3 33.1 19M22 3 37.6 19M22 3 42 19' />
                      <path className='bf-d' d='M2 19v0M6.4 19v0M10.9 19v0M15.3 19v0M19.8 19v0M24.2 19v0M28.7 19v0M33.1 19v0M37.6 19v0M42 19v0' />
                    </svg>
                    <b data-backup-b>{strings.backupRest[0]}</b>
                    <strong>10</strong>
                    <small className='mc-backup-sub'>
                      <span data-backup-sub>{strings.backupRest[1]}</span>
                      {[strings.backupRest[1], strings.backupHot[1], strings.backupLone[1], strings.backupNone[1]].map((ghost, index) => (
                        <span key={index} className='mc-node-ghost' aria-hidden='true'>
                          {ghost}
                        </span>
                      ))}
                    </small>
                  </div>
                </div>
                <div className='mc-result' aria-hidden='true'>
                  <p className='mc-direct-key'>{t('mi.result_key')}</p>
                  <div className='mc-direct' data-sum-row>
                    <i />
                    <span className='mc-direct-txt'>
                      <b data-sum-b>{strings.sumContinuous}</b>
                      <span className='x-sep' />
                      {/* eslint-disable-next-line react/no-danger -- the engine owns this node's markup from here on; the first paint is our own translation */}
                      <span className='mc-direct-sub' data-sum-sub dangerouslySetInnerHTML={{ __html: `<span class="x-long">${strings.sumSubPair(combined)[0]}</span><span class="x-short">${strings.sumSubPair(combined)[1]}</span>` }} />
                    </span>
                    <strong>
                      <span data-sum>800</span>
                      <span className='u' data-sum-u>
                        {strings.mbps}
                      </span>
                    </strong>
                  </div>
                </div>
              </div>

              <footer className='mc-foot'>
                <div className='mc-foot-text'>
                  <p className='mc-status' data-status>
                    <i aria-hidden='true' />
                    {/* eslint-disable-next-line react/no-danger -- the engine rewrites this line; the first paint is our own translation */}
                    <span className='mc-status-body' data-status-text dangerouslySetInnerHTML={{ __html: `<b class="mi-stab">${strings.stabOk}</b> · ${t('mi.status_initial')}` }} />
                  </p>
                  <div className='mc-foot-slot'>
                    <p className='mc-bridge' data-bridge>
                      {t('mi.bridge')}
                    </p>
                  </div>
                </div>
                <div className='mc-actions'>
                  <button className='mc-pause' type='button' data-pause aria-label={strings.pauseAria} aria-pressed='false' title={t('mi.pause_title')}>
                    <svg className='ic-pause' viewBox='0 0 24 24' aria-hidden='true'>
                      <path d='M8.5 5.5v13M15.5 5.5v13' />
                    </svg>
                    <svg className='ic-play' viewBox='0 0 24 24' aria-hidden='true'>
                      <path d='M8 5.2v13.6a.8.8 0 0 0 1.2.7l11-6.8a.8.8 0 0 0 0-1.4l-11-6.8a.8.8 0 0 0-1.2.7z' />
                    </svg>
                  </button>
                  <button className='btn btn-secondary mc-btn' type='button' data-drop aria-busy='false' title={strings.dropTitleDefault}>
                    <svg viewBox='0 0 24 24' aria-hidden='true'>
                      <path d='M13.5 3 6 13h5l-1 8L18 11h-5z' />
                      <path d='M4 4l16 16' />
                    </svg>
                    <span className='mc-btn-lbl'>
                      <span data-lbl>{strings.dropLabel}</span>
                      {[strings.dropLabel, strings.reconnectingLabel, strings.reconnectLabel].map((ghost, index) => (
                        <span key={index} className='mc-btn-ghost' aria-hidden='true'>
                          {ghost}
                        </span>
                      ))}
                    </span>
                  </button>
                  <div className='mc-offer'>
                    <a
                      className='btn btn-primary mc-cta'
                      data-cta
                      href='#np-form'
                      onClick={(event) => {
                        event.preventDefault();
                        onCta();
                      }}
                    >
                      <span>{t('common.cta')}</span>
                      <svg viewBox='0 0 24 24' aria-hidden='true'>
                        <path d='M5 12h13M13 6l6 6-6 6' />
                      </svg>
                    </a>
                    <p className='mc-offer-note'>
                      <Rich i18nKey='mi.offer_note' />
                    </p>
                  </div>
                </div>
                <p className='sr-only' role='status' aria-live='polite' data-live />
              </footer>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
}
